import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import FadeInSection from '../components/FadeInSection';
import PageBanner from '../components/PageBanner';
import { cocktailImageUrl } from '../utils/cocktailImage';
import { useCms } from '../context/CmsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CocktailMenu = () => {
  const { img, cocktailCategories } = useCms();
  const [cocktails, setCocktails] = useState([]);
  const [filteredCocktails, setFilteredCocktails] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [selectedCocktail, setSelectedCocktail] = useState(null);
  const [loading, setLoading] = useState(true);

  const categories = ['Alle', ...(cocktailCategories && cocktailCategories.length ? cocktailCategories : ['Klassiek', 'Fruitig', 'Sterk', 'Zoet', 'Mocktails'])];

  useEffect(() => {
    const fetchCocktails = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/cocktails`);
        setCocktails(res.data);
        setFilteredCocktails(res.data);
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchCocktails();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'Alle') {
      setFilteredCocktails(cocktails);
    } else {
      setFilteredCocktails(
        cocktails.filter((c) => {
          const cats = (c.categorieen && c.categorieen.length) ? c.categorieen : (c.categorie ? [c.categorie] : []);
          return cats.includes(selectedCategory);
        })
      );
    }
  }, [selectedCategory, cocktails]);

  return (
    <div>
      <PageBanner
        image={img('banner.menu')}
        title="Ons Cocktailmenu"
        subtitle="Van klassieke favorites tot unieke creaties — er is voor ieder wat wils"
      />

      {/* Filter */}
      <section className="bg-white sticky top-20 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  selectedCategory === cat
                    ? 'bg-[#13498f] text-white shadow-summer'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Cocktails Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center py-20">Loading...</div>
        ) : (
          <FadeInSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCocktails.map((cocktail) => (
              <div 
                key={cocktail.id} 
                className="bg-white rounded-lg shadow-summer overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105"
                onClick={() => setSelectedCocktail(cocktail)}
              >
                <div 
                  className="h-64 bg-cm-primary"
                  style={{
                    backgroundImage: `url("${cocktailImageUrl(cocktail)}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                ></div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-2xl font-bold text-[#13498f]">{cocktail.naam}</h3>
                    {cocktail.populair && (
                      <span className="bg-[#FFB84D] text-[#13498f] px-2 py-1 rounded text-xs font-bold">TOP</span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">{cocktail.beschrijving}</p>
                  {cocktail.alcohol_percentage === 0 && (
                    <span className="text-sm font-semibold text-[#2D7A5F]">Alcoholvrij</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          </FadeInSection>
        )}
      </section>

      {/* Cocktail Detail Modal */}
      <Dialog open={!!selectedCocktail} onOpenChange={() => setSelectedCocktail(null)}>
        <DialogContent className="max-w-2xl">
          {selectedCocktail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-bold text-[#13498f]">
                  {selectedCocktail.naam}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div 
                  className="h-64 bg-[#13498f] rounded-lg"
                  style={{
                    backgroundImage: `url("${cocktailImageUrl(selectedCocktail)}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                </div>
                <p className="text-gray-700 text-lg">{selectedCocktail.beschrijving}</p>
                <div>
                  <h4 className="font-bold text-lg mb-2 text-[#13498f]">Ingrediënten:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedCocktail.ingredienten.map((ing, idx) => (
                      <li key={idx} className="text-gray-700">{ing}</li>
                    ))}
                  </ul>
                </div>
                {selectedCocktail.alcohol_percentage === 0 && (
                  <div className="pt-4 border-t">
                    <span className="font-semibold text-[#2D7A5F]">Alcoholvrij</span>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CocktailMenu;